/**
 * The single source of every brand value used by the Sellary shell and the
 * Accounts page. No imports on purpose: `vuetify/lib` is ESM and Jest
 * transpiles nothing under `node_modules` except `aws-amplify`
 * (jest.config.js), so anything importing Vuetify cannot be loaded in a
 * test. This file can be, and every value in it is checked by
 * `__tests__/tokens.test.js` -- contrast ratios included, not just presence.
 *
 * See docs/superpowers/specs/OPT-24-spec.md and OPT-24-log.md for why each
 * status color is a {fill, text, tint} triple rather than one hex reused for
 * both text and background: a pill's text against its own tint has to clear
 * 4.5:1, and the base brand hues do not on their own.
 */

export const color = {
  primary: "#1F7F7A", // brand-600: filled primary button, active nav pill, focus ring, links
  primaryBar: "#398487", // logo teal, decorative
  accent: "#E96658", // coral-500. brand accent ONLY -- never text, never a button
  secondary: "#C9403E", // coral-700: the lightest coral that carries white text (4.5:1)
  money: "#B45309", // amber-700: prices and money figures
  ink: "#0F172A", // slate-900: headings, figures
  muted: "#64748B", // slate-500: labels, secondary text. See "muted has almost no headroom" below.
  surface: "#F8FAFC", // slate-50: page background
  card: "#FFFFFF",
  border: "#E2E8F0", // slate-200: hairline
};

/**
 * Every ratio below was computed with a WCAG relative-luminance formula, not
 * estimated. `color.primary` (#1F7F7A) is 4.80:1 against white and works as
 * the FILLED BUTTON; `tint.primary.text` below is a separate, darker value
 * because brand-600 on brand-50 clears 4.5:1 by only 0.01.
 *
 * `muted` (#64748B) has almost no headroom: 4.76:1 on `#FFFFFF` but only
 * 4.55:1 on the `#F8FAFC` page surface. Both pass, neither by much. Do not
 * put muted text on any background darker than `#F8FAFC`, and re-run the
 * contrast test in __tests__/tokens.test.js before changing any tint here.
 */
export const status = {
  healthy: { fill: "#2E7D5B", text: "#17503A", tint: "#E3EAE7" }, // 7.66:1
  warn: { fill: "#B45309", text: "#92400E", tint: "#FFFBEB" }, // amber-700/800/50, 6.84:1
  error: { fill: "#B3261E", text: "#842029", tint: "#F0E4E5" }, // 7.63:1
  offline: { fill: "#64748B", text: "#475569", tint: "#F1F5F9" }, // slate-500/600/100, 6.92:1
};

export const tint = {
  primary: { text: "#186562", bg: "#EFFAF9" }, // brand-700 on brand-50, 6.41:1
  accent: { text: "#A53534", bg: "#FEF2F1" }, // coral-800 on coral-50, 6.07:1
};

// OPT-386: the My Team metric tiles. `bg` is the tile, `icon` the icon disc, `ink` the icon stroke, chevron
// and (amber only) the figure. `tileLabel` is the caption colour: color.muted is under 4.5:1 on every tile bg.
export const tile = {
  blue: { bg: "#EFF6FF", icon: "#DBEAFE", ink: "#1D4ED8" },
  green: { bg: "#EEF7F2", icon: "#D5EDE0", ink: "#17503A" },
  amber: { bg: "#FFF7ED", icon: "#FFEDD5", ink: "#B45309" },
  purple: { bg: "#F5F3FF", icon: "#EDE9FE", ink: "#6D28D9" },
};
export const tileLabel = "#475569";

export const radius = {
  card: "14px",
  pill: "999px",
  control: "10px",
};

export const typography = {
  // Live Assist's typography (sellary-live-assist/DESIGN.md), per OPT-225.
  // Roboto stays linked in public/index.html because Vuetify's own
  // components reference it.
  fontFamily: '"Plus Jakarta Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
  monoFamily: '"Geist Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
};

export const tokens = { color, status, tint, tile, tileLabel, radius, typography };

export default tokens;
